import { useDirection } from '@repo/ui/direction';
import { cn } from '@repo/ui/lib/utils';

type WizardProgressBarProps = {
  /** Zero-based index of the currently active step */
  currentIndex: number;
  totalSteps: number;
  className?: string;
};

/**
 * A standalone progress bar that mirrors the visual style of the Stepper
 * component (chevron segments) but accepts plain props instead of requiring
 * a StepperProvider ancestor.
 */
export function WizardProgressBar({ currentIndex, totalSteps, className }: WizardProgressBarProps) {
  const direction = useDirection();
  const arrowDepth = 12;
  const gapSize = 2;
  const overlap = arrowDepth - gapSize;

  return (
    <div className={cn('flex h-3 w-full overflow-hidden rounded-full md:h-4', className)}>
      {Array.from({ length: totalSteps }, (_, index) => {
        const isActive = currentIndex === index;
        const isFirst = index === 0;
        const isLast = index === totalSteps - 1;
        const isEvenIndex = index % 2 === 0;

        let clipPath = '';

        if (isFirst) {
          clipPath = `polygon(0 0, calc(100% - ${arrowDepth}px) 0, 100% 50%, calc(100% - ${arrowDepth}px) 100%, 0 100%)`;
        } else if (isLast) {
          if (isEvenIndex) {
            clipPath = `polygon(0 50%, ${arrowDepth}px 0, 100% 0, 100% 100%, ${arrowDepth}px 100%)`;
          } else {
            clipPath = `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrowDepth}px 50%)`;
          }
        } else {
          if (isEvenIndex) {
            clipPath = `polygon(0 50%, ${arrowDepth}px 0, calc(100% - ${arrowDepth}px) 0, 100% 50%, calc(100% - ${arrowDepth}px) 100%, ${arrowDepth}px 100%)`;
          } else {
            clipPath = `polygon(0 0, 100% 0, calc(100% - ${arrowDepth}px) 50%, 100% 100%, 0 100%, ${arrowDepth}px 50%)`;
          }
        }

        return (
          <div
            className={cn(
              'relative h-full flex-1 transition-colors duration-300 ease-in-out',
              isActive ? 'bg-primary' : index < currentIndex ? 'bg-primary/60' : 'bg-primary/20',
            )}
            key={index}
            style={{
              clipPath,
              transform: direction === 'rtl' ? 'scaleX(-1)' : undefined,
              marginInlineStart: isFirst ? 0 : `-${overlap}px`,
              zIndex: isEvenIndex ? 10 : 1,
            }}
          />
        );
      })}
    </div>
  );
}
